import { CartAddEvent } from '@theme/events';

const RECOMMENDATIONS_SECTION = 'cart-upsell-recommendations';

/** @type {Map<string, string>} recommendations HTML keyed by request URL */
const recommendationsCache = new Map();

/**
 * "Complete the look" upsell shown in the cart drawer and on the cart page.
 * Starts with the products picked in theme settings, then swaps in recommendations
 * for the most recently added cart item when Shopify has some.
 */
class CartUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  #observer = new MutationObserver(() => this.#onMorph());
  #recommendedItems = /** @type {Element[] | null} */ (null);

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#observer.disconnect();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) {
      this.#recommendedItems = null;
      this.#loadRecommendations();
    }
  }

  get track() {
    return this.querySelector('[data-cart-upsell-track]');
  }

  get cartProductIds() {
    return new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
  }

  async #loadRecommendations() {
    const { productId, recommendationsUrl } = this.dataset;
    if (!productId || !recommendationsUrl) return;

    const url = `${recommendationsUrl}?product_id=${productId}&limit=10&intent=related&section_id=${RECOMMENDATIONS_SECTION}`;

    try {
      let html = recommendationsCache.get(url);
      if (html === undefined) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
        html = await response.text();
        recommendationsCache.set(url, html);
      }

      // The cart may have changed while the request was in flight
      if (this.dataset.productId !== productId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.#recommendedItems = Array.from(doc.querySelectorAll('.cart-upsell__item'));
      this.#applyRecommendations();
    } catch (error) {
      // Keep the products picked in theme settings
      console.warn('Cart upsell:', error);
    }
  }

  #applyRecommendations() {
    const track = this.track;
    if (!track || !this.#recommendedItems) return;

    const inCart = this.cartProductIds;
    const items = this.#recommendedItems.filter((item) => !inCart.has(item.getAttribute('data-product-id') || ''));
    if (items.length === 0) return;

    this.#observer.disconnect();
    track.replaceChildren(
      ...items.map((item) => {
        const clone = /** @type {HTMLElement} */ (document.importNode(item, true));
        clone.dataset.recommended = '';
        return clone;
      })
    );
    track.scrollLeft = 0;
    this.hidden = false;
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  /** The theme re-renders the cart after every change, which puts the fallback products back */
  #onMorph() {
    const track = this.track;
    const showingFallback = track?.querySelector('.cart-upsell__item:not([data-recommended])');
    if (showingFallback && this.#recommendedItems) {
      queueMicrotask(() => this.#applyRecommendations());
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLElement} */ (event.target).closest('[data-upsell-variant]');
    if (!(select instanceof HTMLSelectElement)) return;

    const item = select.closest('.cart-upsell__item');
    const price = item?.querySelector('[data-upsell-price]');
    const button = item?.querySelector('[data-upsell-add]');
    const option = select.selectedOptions[0];

    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (button instanceof HTMLElement) button.dataset.variantId = select.value;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const arrow = target.closest('[data-upsell-arrow]');
    if (arrow instanceof HTMLElement) {
      const track = this.track;
      track?.scrollBy({ left: track.clientWidth * Number(arrow.dataset.upsellArrow), behavior: 'smooth' });
      return;
    }

    const button = target.closest('[data-upsell-add]');
    if (button instanceof HTMLButtonElement && !button.disabled) this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const item = button.closest('.cart-upsell__item');
    const error = item?.querySelector('.cart-upsell__error');
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    if (error instanceof HTMLElement) error.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'This item could not be added to your cart.');
      }

      const cart = await (await fetch(`${root}cart.js`)).json();

      this.dispatchEvent(
        new CartAddEvent(cart, variantId, {
          source: 'cart-upsell',
          itemCount: cart.item_count,
          variantId,
          sections: data.sections,
        })
      );
    } catch (caught) {
      if (error instanceof HTMLElement) {
        error.textContent = caught instanceof Error ? caught.message : String(caught);
        error.hidden = false;
      }
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}